import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Grid, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { useAxisTooltip } from "@mui/x-charts/ChartsTooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareIcon from "@mui/icons-material/Share";
import InsightsIcon from "@mui/icons-material/Insights";

import { hostname } from "../../utils";
import {
  CONTENT_METRICS_TITLE,
  CONTENT_METRICS_SUBTITLE_EMPTY,
  DEMO_MODE_KEY,
} from "../../constants";
import {
  DEMO_CONTENT_METRICS,
  generateDemoContentTimeseries,
} from "../../constants/demoData";

const CompactMetric = ({
  icon,
  title,
  value,
  tooltip,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  tooltip: string;
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
        <Box sx={{ color: "text.secondary", display: "flex", "& svg": { fontSize: 16 } }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

interface ContentMetrics {
  views: number;
  reach: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  trackedPosts: number;
}

interface TimeseriesSeries {
  contentId: string;
  title: string;
  publishedUrl: string | null;
  platform: string;
  label: string;
  data: number[];
}

interface TimeseriesResponse {
  dates: string[];
  series: TimeseriesSeries[];
}

interface Props {
  companyId: string;
  getToken: () => Promise<string>;
  showMetrics?: boolean;
}

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

// Matches the sequence metrics palette (SequenceMetrics.tsx) so the two charts feel like a set.
const SEQUENCE_PALETTE = ["#1976d2", "#0288d1", "#00c853", "#ff9800"];

const shortenTitle = (title: string, publishedUrl: string | null): string => {
  if (publishedUrl) {
    try {
      const u = new URL(publishedUrl);
      const slug = u.pathname.split("/").filter(Boolean).pop();
      if (slug) {
        const pretty = decodeURIComponent(slug).replace(/[-_]+/g, " ");
        if (pretty.length <= 32) return pretty;
      }
    } catch {
      /* fall through */
    }
  }
  if (title.length <= 32) return title;
  return title.slice(0, 30).trimEnd() + "…";
};

const formatShortDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const SortedAxisTooltipContent: React.FC = () => {
  const tooltipData = useAxisTooltip();
  if (!tooltipData) return null;
  const { axisFormattedValue, seriesItems } = tooltipData;
  const rows = seriesItems
    .filter((item) => typeof item.value === "number" && item.value > 0)
    .sort((a, b) => (b.value as number) - (a.value as number));
  if (rows.length === 0) return null;
  return (
    <Paper
      elevation={3}
      sx={{ px: 1.25, py: 0.75, minWidth: 140, pointerEvents: "none" }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
      >
        {axisFormattedValue}
      </Typography>
      {rows.map((row) => (
        <Box
          key={row.seriesId}
          sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.25 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: row.color,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.formattedLabel}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {formatNumber(row.value as number)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

const ContentMetricsBar: React.FC<Props> = ({ companyId, getToken, showMetrics = true }) => {
  const [metrics, setMetrics] = useState<ContentMetrics | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };
    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isDemoMode) {
      setLoading(true);
      const timer = setTimeout(() => {
        if (!cancelled) {
          setMetrics(DEMO_CONTENT_METRICS);
          setTimeseries(generateDemoContentTimeseries());
          setLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    const load = async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const [statsRes, seriesRes] = await Promise.all([
          axios.get(`${hostname}/content/statistics`, {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${hostname}/content/timeseries`, {
            params: { companyId, days: 7 },
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!cancelled) {
          setMetrics(statsRes.data.metrics ?? null);
          setTimeseries(seriesRes.data ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setMetrics(null);
          setTimeseries(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, getToken, isDemoMode]);

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        {showMetrics && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Skeleton variant="rounded" height={56} />
              </Grid>
            ))}
          </Grid>
        )}
        <Skeleton variant="rounded" height={160} />
      </Box>
    );
  }

  if (!metrics || metrics.trackedPosts === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InsightsIcon sx={{ color: "text.secondary", fontSize: 18 }} />
        <Typography variant="caption" color="text.secondary">
          {CONTENT_METRICS_TITLE} — {CONTENT_METRICS_SUBTITLE_EMPTY}
        </Typography>
      </Paper>
    );
  }

  const chartSeries =
    timeseries?.series
      .filter((s) => s.data.some((v) => v > 0))
      .sort(
        (a, b) =>
          b.data.reduce((sum, v) => sum + v, 0) -
          a.data.reduce((sum, v) => sum + v, 0)
      )
      .map((s, i) => ({
        label: shortenTitle(s.title, s.publishedUrl),
        data: s.data,
        color: SEQUENCE_PALETTE[i % SEQUENCE_PALETTE.length],
        stack: "views",
        area: true,
        showMark: false,
        curve: "monotoneX" as const,
      })) ?? [];

  const chartDates = timeseries?.dates.map(formatShortDate) ?? [];
  const hasChartData = chartSeries.length > 0;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {showMetrics && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<VisibilityIcon />}
              title="Views"
              value={formatNumber(metrics.views)}
              tooltip="Total views across tracked published posts"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<PeopleAltIcon />}
              title="Reach"
              value={formatNumber(metrics.reach)}
              tooltip="Unique accounts reached"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<FavoriteIcon />}
              title="Likes"
              value={formatNumber(metrics.likes)}
              tooltip="Total likes/reactions"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<ChatBubbleOutlineIcon />}
              title="Comments"
              value={formatNumber(metrics.comments)}
              tooltip="Total comments"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<ShareIcon />}
              title="Shares"
              value={formatNumber(metrics.shares)}
              tooltip="Total shares"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<InsightsIcon />}
              title="Engagement"
              value={formatNumber(metrics.engagement)}
              tooltip="Total engagement (likes + comments + shares)"
            />
          </Grid>
        </Grid>
      )}
      <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
        {hasChartData ? (
          <Box sx={{ width: "100%", height: 160 }}>
            <LineChart
              xAxis={[{ scaleType: "point", data: chartDates }]}
              yAxis={[{ disableLine: true, disableTicks: true }]}
              series={chartSeries}
              height={160}
              margin={{ top: 10, right: 10, bottom: 24, left: 10 }}
              slotProps={{ legend: { hidden: true } }}
              slots={{ axisContent: SortedAxisTooltipContent }}
              sx={{
                "& .MuiAreaElement-root": { fillOpacity: 0.35 },
                "& .MuiChartsAxis-tickLabel": { fontSize: "0.7rem" },
                "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": { display: "none" },
              }}
            />
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 1, textAlign: "center" }}>
            No traffic in the last 7 days yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default ContentMetricsBar;
